import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Check, ArrowRight, Trophy, Compass, Sparkle, Clock
} from '@phosphor-icons/react';
import { Card, CardContent } from '../components/ui/card';
import { API_URL } from '../config/api';

/**
 * Your First Steps — a workflow-oriented getting-started guide.
 * Replaces the old "Explore TurfPulse" feature tour.
 *
 * Items are grouped into phases (Build → Diagnose → Plan → Habit)
 * and numbered so a brand-new user knows exactly what order to do things in.
 */
export const ExploreTurfPulse = () => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API_URL}/api/explore/progress`, { withCredentials: true })
      .then((res) => {
        setData(res.data);
        // Auto-expand the first phase with incomplete items
        const phaseOrder = Object.entries(res.data.phases || {})
          .sort((a, b) => a[1].order - b[1].order)
          .map(([key]) => key);
        const firstIncompletePhase = phaseOrder.find((p) =>
          res.data.items.some((i) => i.phase === p && !i.completed)
        );
        setExpandedPhase(firstIncompletePhase || phaseOrder[0]);
      })
      .catch(() => {});
  }, []);

  if (!data || dismissed) return null;

  const pct = Math.round((data.completed / data.total) * 100);
  const allDone = data.completed === data.total;
  const phases = data.phases || {};

  // Group items by phase, preserving step order
  const itemsByPhase = {};
  data.items.forEach((item) => {
    const key = item.phase || 'other';
    if (!itemsByPhase[key]) itemsByPhase[key] = [];
    itemsByPhase[key].push(item);
  });

  const phaseKeys = Object.keys(phases).sort(
    (a, b) => phases[a].order - phases[b].order
  );

  const handleClick = async (item) => {
    if (!item.completed) {
      await axios
        .post(
          `${API_URL}/api/explore/complete`,
          { item_id: item.id },
          { withCredentials: true }
        )
        .catch(() => {});
      setData((prev) => ({
        ...prev,
        items: prev.items.map((i) =>
          i.id === item.id ? { ...i, completed: true } : i
        ),
        completed: prev.completed + 1,
      }));
    }
    navigate(item.path);
  };

  // Compute phase-level progress
  const phaseProgress = (phaseKey) => {
    const phaseItems = itemsByPhase[phaseKey] || [];
    const done = phaseItems.filter((i) => i.completed).length;
    return { done, total: phaseItems.length, complete: done === phaseItems.length && phaseItems.length > 0 };
  };

  return (
    <Card data-testid="explore-card" className="overflow-hidden border-2 border-primary/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            {allDone ? (
              <Trophy size={28} weight="fill" className="text-yellow-300 shrink-0 mt-0.5" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                <Compass size={22} weight="duotone" className="text-white" />
              </div>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-100 mb-0.5">
                {allDone ? 'TurfPulse Explorer' : 'Start here'}
              </p>
              <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                {allDone
                  ? "You've mastered TurfPulse — nice work."
                  : 'Your First Steps'}
              </h3>
              <p className="text-emerald-100 text-xs sm:text-sm mt-1">
                {allDone
                  ? 'Every corner of the app explored. Keep tracking and stay ahead of the season.'
                  : 'A short, ordered walkthrough so you know exactly where to start.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <span className="text-white text-lg font-bold tracking-tight">
                {data.completed}<span className="text-emerald-200 text-sm font-normal">/{data.total}</span>
              </span>
              <p className="text-[10px] text-emerald-200 uppercase tracking-wider">done</p>
            </div>
            {allDone && (
              <button
                onClick={() => setDismissed(true)}
                className="text-emerald-200 text-xs hover:text-white transition-colors underline underline-offset-2"
                data-testid="explore-dismiss"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
        {/* Overall progress */}
        <div className="mt-4 h-1.5 rounded-full bg-white/15 overflow-hidden">
          <div
            className="h-full rounded-full bg-white transition-all duration-700"
            style={{ width: `${pct}%` }}
            data-testid="explore-progress-bar"
          />
        </div>
      </div>

      {/* Phases */}
      <CardContent className="p-0">
        <div className="divide-y divide-border/40">
          {phaseKeys.map((phaseKey) => {
            const phase = phases[phaseKey];
            const { done, total, complete } = phaseProgress(phaseKey);
            const isExpanded = expandedPhase === phaseKey;
            const items = itemsByPhase[phaseKey] || [];

            return (
              <div key={phaseKey} data-testid={`explore-phase-${phaseKey}`}>
                {/* Phase header (clickable to toggle) */}
                <button
                  onClick={() =>
                    setExpandedPhase((prev) => (prev === phaseKey ? null : phaseKey))
                  }
                  className={`w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors ${
                    complete ? 'bg-emerald-50 dark:bg-emerald-950/20' : 'hover:bg-muted/40'
                  }`}
                  data-testid={`explore-phase-toggle-${phaseKey}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        complete
                          ? 'bg-emerald-500 text-white'
                          : done > 0
                            ? 'bg-primary/15 text-primary'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {complete ? <Check size={14} weight="bold" /> : phase.order}
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                        {phase.eyebrow}
                      </p>
                      <p className="text-sm font-semibold">{phase.label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{done}/{total}</span>
                    <ArrowRight
                      size={14}
                      className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  </div>
                </button>

                {/* Items (collapsible) */}
                {isExpanded && (
                  <div className="bg-muted/20 border-t border-border/40">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleClick(item)}
                        className={`w-full flex items-center gap-3 px-5 py-3 pl-14 text-left transition-colors hover:bg-background ${
                          item.completed ? 'opacity-60' : ''
                        }`}
                        data-testid={`explore-item-${item.id}`}
                      >
                        <div
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            item.completed
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-muted-foreground/30'
                          }`}
                        >
                          {item.completed ? (
                            <Check size={12} weight="bold" />
                          ) : (
                            <span className="text-[10px] font-bold text-muted-foreground">
                              {String(item.step || '').padStart(2, '0')}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium ${
                              item.completed ? 'line-through text-muted-foreground' : ''
                            }`}
                          >
                            {item.label}
                          </p>
                          <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                            {item.desc}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {item.minutes && !item.completed && (
                            <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                              <Clock size={10} /> {item.minutes}m
                            </span>
                          )}
                          {!item.completed && (
                            <ArrowRight size={14} className="text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer tip */}
        {!allDone && (
          <div className="px-5 py-3 bg-muted/30 border-t border-border/40 flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkle size={12} weight="fill" className="text-primary" />
            <span>
              Steps auto-check as you use the app — no need to mark them manually.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
